/**
 * Defines the contract for device-specific BLE operations.
 */

import { BleClient } from '@capacitor-community/bluetooth-le';
import type { Observable } from 'rxjs';
import { from, of, switchMap, throwError } from 'rxjs';

import type { BleDeviceInfo, BleServiceResponse, DeviceData, DeviceType } from '../../../interfaces/ble.interface';

export abstract class BaseDeviceAdapter {
  abstract readonly deviceType: DeviceType;
  abstract readonly deviceNamePrefix: string;
  abstract readonly identifyingService: string;

  /**
   * Connect to the device and perform device-specific initialization
   */
  abstract connect$(deviceId: string, onDisconnect: (deviceId: string) => void): Observable<BleServiceResponse<BleDeviceInfo>>;

  /**
   * Disconnect from the device and cleanup
   */
  abstract disconnect$(deviceId: string): Observable<BleServiceResponse<void>>;

  /**
   * Read battery level (0-100%)
   */
  abstract readBatteryLevel$(deviceId: string): Observable<BleServiceResponse<number>>;

  /**
   * Read brightness level
   */
  abstract readBrightnessLevel$(deviceId: string): Observable<BleServiceResponse<number>>;

  /**
   * Write brightness level
   */
  abstract writeBrightnessLevel$(deviceId: string, level: number): Observable<BleServiceResponse<void>>;

  /**
   * Read device information (manufacturer, model, serial, etc.)
   */
  abstract readDeviceInfo$(deviceId: string): Observable<BleServiceResponse<Partial<DeviceData>>>;

  /**
   * Load initial data for this device type.
   * Each adapter reads its specific data and pushes partial updates via updateData.
   */
  abstract loadInitialData$(deviceId: string, updateData: (data: Partial<DeviceData>) => void): Observable<void>;

  canHandleDevice(deviceName: string, advertisedServices?: Array<string>): boolean {
    if (advertisedServices?.some(uuid => uuid.toLowerCase() === this.identifyingService)) {
      return true;
    }
    return deviceName.startsWith(this.deviceNamePrefix);
  }

  protected discoverAndValidateServices$(deviceId: string): Observable<void> {
    return from(BleClient.getServices(deviceId)).pipe(
      switchMap(services => {
        if (services.length === 0) {
          return throwError(() => new Error('No services discovered on device'));
        }
        return of(void 0);
      })
    );
  }
}
